import { useRef, useEffect } from "react";
import { debug } from "../../../../utils/debugLogger";

export interface JobStatusPollingOptions {
  jobId: string;
  customerId: string;
  agentId: string;
  idToken: string;
  fetchAgentDetails: (
    agentId: string,
    customerId: string,
    idToken: string
  ) => Promise<any>;
  onSuccess: (details: any) => void;
  onFailure: (errorMessage: string) => void;
  onStatusUpdate?: (status: string) => void;
  pollInterval?: number; // in milliseconds
  timeout?: number; // in milliseconds
}

/**
 * Reusable hook for polling job status
 */
export const useJobStatusPolling = () => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startPolling = (options: JobStatusPollingOptions) => {
    const {
      jobId,
      customerId,
      agentId,
      idToken,
      fetchAgentDetails,
      onSuccess,
      onFailure,
      onStatusUpdate,
      pollInterval = 10000, // Default 10 seconds
      timeout = 5 * 60 * 1000, // Default 5 minutes
    } = options;

    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const pollJobStatus = async () => {
      try {
        const details = await fetchAgentDetails(agentId, customerId, idToken);

        const jobStatus = details.training_files_job_status;
        const currentJobId = details.training_files_job_id;

        // Check if this is the job we're tracking
        if (currentJobId !== jobId) {
          // Different job, continue polling
          return;
        }

        if (jobStatus === "SUCCEEDED") {
          // Clear polling interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          onSuccess(details);
        } else if (jobStatus === "FAILED") {
          // Clear polling interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          const errorMessage =
            details.training_files_job_error ||
            "Failed to process job";
          onFailure(errorMessage);
        } else if (jobStatus === "PENDING" || jobStatus === "RUNNING") {
          // Job still in progress, continue polling
          if (onStatusUpdate) {
            onStatusUpdate(jobStatus);
          }
        } else if (jobStatus) {
          // Status exists but is not a known value, continue polling
          debug.warn(`Unexpected job status: ${jobStatus}. Continuing to poll.`);
          if (onStatusUpdate) {
            onStatusUpdate(jobStatus);
          }
        } else {
          // No status yet, continue polling
          if (onStatusUpdate) {
            onStatusUpdate("PENDING");
          }
        }
      } catch (error) {
        debug.error("Error polling job status:", error);
        // Continue polling on error (might be transient)
      }
    };

    // Start polling immediately, then at intervals
    pollJobStatus();
    pollingIntervalRef.current = setInterval(pollJobStatus, pollInterval);

    // Set a maximum polling timeout
    setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        onFailure(
          "Timeout waiting for job to complete. Please refresh and check the status."
        );
      }
    }, timeout);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return { startPolling, stopPolling };
};
